import AddEmailToPersonModal from '@/components/AddEmailToPersonModal';
import CreateLeverageRequestModal, { LeverageRequestPromise } from '@/components/CreateLeverageRequestModal';
import IconStarred from '@/components/IconStarred';
import InputDuration from '@/components/InputDuration';
import ManageActionRelations from '@/components/ManageActionRelations';
import ProjectsMenu from '@/components/ProjectsMenu';
import RPMDateTimePickerWithPlaceholder from '@/components/RPMDateTimePicker/RPMDateTimePickerWithPlaceholder';
import type { RecurrenceFrequencyApi } from '@/components/CustomRecurrenceModal/CustomRecurrenceModal';
import RecurrenceMenu from '@/components/RecurrenceMenu/RecurrenceMenu';
import Textarea from '@/components/Textarea';
import { ActionRelation, INCOMPLETE } from '@/constants/action';
import { ActionControleSlideYInOutVariants } from '@/constants/animations';
import { DAY, WEEK, weekdayOnly } from '@/constants/calendar';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { CustomRecurrenceData } from '@/gql/events/types';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import useIsDarkMode from '@/hooks/useIsDarkMode';
import { useCreateAction, useCreatePromises } from '@/services/action/hooks';
import { useCreateEvent } from '@/services/events/hooks';
import { usePeople } from '@/services/people/hooks';
import { getWeeklyPlanId, useWeeklyPlanId } from '@/services/plans/hooks/useWeeklyPlan';
import { useUserInfo } from '@/services/user/hooks';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import { IconPlus } from '@/theme/icons';
import { DayWeek } from '@/types/calendar';
import { Category } from '@/types/category';
import { ProjectType } from '@/types/project';
import { UserGroup } from '@/types/user';
import { refetchActions } from '@/utils/action';
import {
  alignLocalDatetimeToFirstWeeklyByDay,
  formatDateToString,
  formatStringToDate,
  getGMTOffset,
  localToGivenTimezoneDateToUTC,
  weeklyByDayAnchorNeedsAlignment,
} from '@/utils/calendar';
import { getColorFromToken } from '@/utils/color';
import { getFrequency, isEveryWeekdayLabel, normalizeRecurrenceRuleFrequency, validateRecurrence } from '@/utils/events';
import mergeRefs from '@/utils/mergeRefs';
import rem from '@/utils/rem';
import { trackActionStarred } from '@/utils/tracking';
import { Box, Button, Checkbox, HStack, Icon, Text, VStack, useDisclosure, useToast } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AnimationControls, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ITimezone } from 'react-timezone-select';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

type Props = {
  selectedCategoryId: string | undefined;
  category: Category | undefined;
  actionCreatorOptionsAnimation: AnimationControls;
  fancyCapturePage?: boolean;
  shouldFocus?: boolean;
  defaultToDayMode?: boolean;
  onAnimationStateChange?: (state: 'visible' | 'exit') => void;
  onDismiss?: () => void;
};

function QuickActionCreator({
  category,
  selectedCategoryId,
  actionCreatorOptionsAnimation,
  fancyCapturePage,
  shouldFocus = true,
  defaultToDayMode = false,
  onAnimationStateChange,
  onDismiss,
}: Props) {
  const { t } = useTranslation('QuickActionCreator');
  const location = useLocation();

  const isFromCategorySpotlight = useMemo(() => {
    return location.pathname.includes('/category-spotlight');
  }, [location.pathname]);

  const QuickActionCreatorFormSchema = z.object({
    name: z
      .string()
      .trim()
      .min(1, { message: t('validation.nameRequired') })
      .max(100, { message: t('validation.nameMaxLength') }),
    time: z
      .string()
      .trim()
      .refine((value) => !!value, {
        message: t('validation.timeRequired'),
      })
      .refine(
        (value) => {
          const [hours, minutes] = value.split(':').map(Number);
          if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
            return false;
          }
          return true;
        },
        {
          message: t('validation.invalidTimeFormat'),
        },
      )
      .refine(
        (value) => {
          const [hours, minutes] = value.split(':').map(Number);

          if (hours === 0 && minutes === 0) {
            return false;
          }
          return true;
        },
        {
          message: t('validation.minimumDuration'),
        },
      ),
    notes: z.string().optional(),
    createLeverageRequest: z.boolean().optional(),
  });

  type FormData = z.infer<typeof QuickActionCreatorFormSchema>;

  const toast = useToast();
  const textAreaValueRef = useRef<HTMLTextAreaElement>(null);
  const { selectedDate } = useCalendarMonthlyStore();
  const [scheduledTime, setScheduledTime] = useState<Date | null>(null);
  const [scheduledDate, setScheduledDate] = useState<Date | null>(selectedDate);
  const [selectedTimezone, setSelectedTimezone] = useState<ITimezone>(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [isStarred, setIsStarred] = useState<Date | null>(null);
  const [relation, setRelation] = useState<ActionRelation>(ActionRelation.None);
  const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectType | null>(null);
  const [leverageRequestPromise, setLeverageRequestPromise] = useState<LeverageRequestPromise>();
  const [tabIndex, setTabIndex] = useState<typeof DAY | typeof WEEK>(defaultToDayMode ? DAY : WEEK);
  const [dateRange, setDateRange] = useState(defaultToDayMode ? false : true);
  const [selectedRecurrence, setSelectedRecurrence] = useState<string>('');
  const isDarkMode = useIsDarkMode();
  const color = getColorFromToken(category?.color, undefined, undefined);

  // Feature flag checks
  const { isEnabled: isEnabledForBeta } = useFeatureFlag(FEATURE_FLAGS.SHOW_RECURRENT_EVENTS_FOR_BETA_USERS);
  const { isEnabled: isEnabledForAll } = useFeatureFlag(FEATURE_FLAGS.SHOW_RECURRENT_EVENTS_FOR_ALL_USERS);
  const { data: userInfo } = useUserInfo();
  const isBetaUser = userInfo?.me?.userGroup === UserGroup.BETA || userInfo?.me?.userGroup === UserGroup.ADMIN;
  const shouldShowRecurrenceFeature = isEnabledForAll || (isEnabledForBeta && isBetaUser);

  const [customRecurrence, setCustomRecurrence] = useState<CustomRecurrenceData>();

  const recurrenceFrequencyForMenu = useMemo((): RecurrenceFrequencyApi | undefined => {
    const unit = customRecurrence?.selectedRecurrence?.toLowerCase();
    if (!unit) return undefined;
    if (unit === 'day') return 'daily';
    if (unit === 'week') return 'weekly';
    if (unit === 'month') return 'monthly';
    if (unit === 'year') return 'yearly';
    return undefined;
  }, [customRecurrence?.selectedRecurrence]);

  const timeZone = typeof selectedTimezone === 'string' ? selectedTimezone : selectedTimezone.value;
  const scheduledDateWithTimeFormatted =
    scheduledTime && localToGivenTimezoneDateToUTC(scheduledTime, timeZone).scheduledDate;

  // always get the weeklyplan for the scheduledDateUpdated if defined, this also means we account
  // for the selected timezone (which could take the date into a different week)
  const weeklyPlanDate = scheduledDateWithTimeFormatted
    ? formatStringToDate(scheduledDateWithTimeFormatted)
    : (scheduledDate ?? new Date());
  const { data: weeklyPlanId } = useWeeklyPlanId(weeklyPlanDate);

  const {
    isOpen: isDatePickerOpen,
    onOpen: onDatePickerOpen,
    onClose: onDatePickerClose,
  } = useDisclosure({ id: 'datepicker' });

  const {
    isOpen: isCreateLeverageModalOpen,
    onOpen: onCreateLeverageModalOpen,
    onClose: onCreateLeverageModalClose,
  } = useDisclosure();

  const { isOpen: isAddEmailModalOpen, onOpen: onAddEmailModalOpen, onClose: onAddEmailModalClose } = useDisclosure();

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    resetField,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      notes: '',
      time: '00:05',
      createLeverageRequest: false,
    },
    resolver: zodResolver(QuickActionCreatorFormSchema),
    mode: 'onChange',
  });

  const createLeverageRequestWatcher = watch('createLeverageRequest');

  const createAction = useCreateAction();
  const createPromises = useCreatePromises();
  const createEvent = useCreateEvent();
  const { data: people } = usePeople();

  // we are only supporting one person at this time but it will most likely change in the future
  // we previously supported selecting more than one person (before the create leverage request feature)
  // so we will keep that work in-tact as we will most likely need it again soon
  const selectedPerson = useMemo(() => {
    if (people?.person && selectedPeople.length) {
      return people.person.find((person) => person.id === selectedPeople[0]);
    }
  }, [people?.person, selectedPeople]);

  const onClickUnplan = useCallback(() => {
    setScheduledDate(null);
    // Clear recurrence when unplanning (removing date)
    if (selectedRecurrence) {
      setSelectedRecurrence('');
      setCustomRecurrence(undefined);
    }
    onDatePickerClose();
  }, [onDatePickerClose, selectedRecurrence]);

  const onChangeTabs = useCallback(
    (index: string | number) => {
      if ((index as DayWeek) === WEEK) {
        setScheduledTime(null);
        // Clear recurrence when switching to WEEK tab (no time)
        if (selectedRecurrence) {
          setSelectedRecurrence('');
          setCustomRecurrence(undefined);
        }
      }

      setTabIndex(index as DayWeek);
      setDateRange((index as DayWeek) === WEEK);
    },
    [selectedRecurrence],
  );

  const handleScheduledTime = useCallback(
    (date: Date | null) => {
      if (!date) {
        setScheduledTime(null);
        // Clear recurrence when time is manually removed
        if (selectedRecurrence) {
          setSelectedRecurrence('');
          setCustomRecurrence(undefined);
        }
        return;
      }

      if (scheduledDate) {
        scheduledDate.setHours(date.getHours());
        scheduledDate.setMinutes(date.getMinutes());
      }

      setScheduledTime(scheduledDate);
    },
    [scheduledDate, selectedRecurrence],
  );

  const handleDatePickerClose = useCallback(() => {
    onDatePickerClose();
  }, [onDatePickerClose]);

  const onStarringAction = () => {
    setIsStarred((prevState) => {
      return prevState ? null : new Date(Date.now());
    });
  };

  const handleResetForm = useCallback(
    (shouldFocus = true) => {
      resetField('name');
      resetField('time');
      setScheduledDate(selectedDate);
      setScheduledTime(null);
      setSelectedRecurrence('');
      setCustomRecurrence(undefined);
      setIsStarred(null);
      setSelectedPeople([]);
      setRelation(ActionRelation.None);
      setSelectedProject(null);
      if (shouldFocus) {
        textAreaValueRef?.current?.focus();
      }
    },
    [resetField, selectedDate],
  );

  const handleCancelClick = useCallback(() => {
    handleResetForm(true);
    onDismiss?.();
  }, [handleResetForm, onDismiss]);

  useEffect(() => {
    setScheduledDate(selectedDate);
    if (defaultToDayMode) {
      setScheduledTime(null);
    }
  }, [selectedDate, defaultToDayMode]);

  useEffect(() => {
    if (defaultToDayMode) {
      setTabIndex(DAY);
      setDateRange(false);
    } else {
      setTabIndex(WEEK);
      setDateRange(true);
      setScheduledTime(null);
    }
  }, [defaultToDayMode, selectedDate]);

  const onSubmit = useCallback(
    (form: FormData) => {
      let currentWeeklyPlan: string | undefined = undefined;

      if (scheduledDate) {
        currentWeeklyPlan = weeklyPlanId;
      }

      if (currentWeeklyPlan == null && scheduledDate) {
        const cachedWeeklyPlanId = getWeeklyPlanId(scheduledDate);

        if (cachedWeeklyPlanId) {
          currentWeeklyPlan = cachedWeeklyPlanId;
        }
      }

      let planAnchorLocal: Date | null = null;
      if (scheduledDate) {
        planAnchorLocal = new Date(scheduledDate);
        if (scheduledTime) {
          planAnchorLocal.setHours(
            scheduledTime.getHours(),
            scheduledTime.getMinutes(),
            scheduledTime.getSeconds(),
            0,
          );
        }
      }

      if (planAnchorLocal && selectedRecurrence) {
        const recurrenceType = selectedRecurrence.toLowerCase();
        const freq = normalizeRecurrenceRuleFrequency(
          getFrequency(recurrenceType, customRecurrence?.selectedRecurrence) || '',
        );
        if (freq === 'weekly') {
          let byDayQuick: { day: string }[] = [];
          if (isEveryWeekdayLabel(recurrenceType)) {
            byDayQuick = weekdayOnly;
          } else if (customRecurrence?.selectedWeekDays?.length) {
            byDayQuick = customRecurrence.selectedWeekDays;
          }
          if (byDayQuick.length > 0 && weeklyByDayAnchorNeedsAlignment(planAnchorLocal, byDayQuick)) {
            planAnchorLocal = alignLocalDatetimeToFirstWeeklyByDay(planAnchorLocal, byDayQuick);
          }
        }
      }

      let newScheduledTZDate: string | undefined;
      if (planAnchorLocal && scheduledTime) {
        newScheduledTZDate = localToGivenTimezoneDateToUTC(planAnchorLocal, timeZone).scheduledDate;
      } else if (planAnchorLocal) {
        newScheduledTZDate = formatDateToString(planAnchorLocal);
      } else if (scheduledDate) {
        newScheduledTZDate = formatDateToString(scheduledDate);
      }

      createAction.mutate(
        {
          blockId: undefined,
          categoryId: category?.id ?? '',
          dateOfStarring: isStarred,
          duration: form.time,
          id: uuidv4(),
          isLocked: false,
          notes: form.notes ?? '',
          progressStatus: INCOMPLETE,
          projectId: selectedProject?.id ?? null,
          scheduledDate: tabIndex === DAY ? (newScheduledTZDate ?? null) : null,
          scheduledTime: scheduledTime ? localToGivenTimezoneDateToUTC(scheduledTime, timeZone).scheduledTime : null,
          timezone: timeZone,
          gmtOffset: getGMTOffset(timeZone),
          title: form.name,
          weeklyPlanId: scheduledDate ? currentWeeklyPlan : undefined,
          isCanceled: false,
        },
        {
          onSuccess: async (data) => {
            const { id: actionId } = data.insertActionOne;

            if (data.insertActionOne.scheduledDate) {
              const recurrenceType = selectedRecurrence?.toLowerCase();

              const isWeekdayOnly = isEveryWeekdayLabel(recurrenceType);
              const recurrenceFrequency = getFrequency(recurrenceType, customRecurrence?.selectedRecurrence);

              let by_day: { day: string }[] = [];

              if (isWeekdayOnly) {
                by_day = weekdayOnly;
              } else if (customRecurrence && customRecurrence?.selectedWeekDays?.length > 0) {
                by_day = customRecurrence.selectedWeekDays;
              }

              const newRecurrenceRule = {
                frequency: recurrenceFrequency,
                interval: customRecurrence?.interval || 1,
                ...(customRecurrence?.occurrences ? { count: customRecurrence.occurrences } : {}),
                ...(by_day.length > 0 ? { by_day } : {}),
                ...(customRecurrence?.scheduledDate
                  ? { until: formatDateToString(customRecurrence.scheduledDate) }
                  : {}),
              };
              const recurrenceForApi = {
                rules: [newRecurrenceRule],
                exceptions: {},
              };
              const recurrenceData =
                recurrenceType && validateRecurrence(recurrenceForApi)
                  ? {
                      recurrence: recurrenceForApi,
                      start: newScheduledTZDate,
                      end: null,
                    }
                  : {};

              const eventData = {
                id: uuidv4(),
                actionId,
                ...(recurrenceData && { ...recurrenceData }),
              };

              const event = await createEvent.mutateAsync(eventData);

              if (event?.insertEventOne) {
                data.insertActionOne.event = {
                  id: event.insertEventOne.id,
                  typeName: 'Event',
                };
              }
            }

            if (relation !== ActionRelation.None && selectedPeople.length) {
              const objects = selectedPeople.map((person) => ({
                id: uuidv4(),
                actionId,
                personId: person,
                kind: relation,
              }));

              const { insertPromise } = await createPromises.mutateAsync({ objects });
              // we are only supporting one "promise" creation (leverage request) at a time right now
              setLeverageRequestPromise(insertPromise.returning[0]);

              data.insertActionOne.promises = insertPromise.returning;
            }

            if (isStarred) {
              trackActionStarred();
            }

            refetchActions(data.insertActionOne);

            toast({ title: t('toasts.actionCreated'), status: 'success', duration: 3000, isClosable: true });

            if (createLeverageRequestWatcher) {
              onCreateLeverageModalOpen();
            } else {
              handleResetForm(true);
            }
          },
        },
      );
    },
    [
      scheduledDate,
      scheduledTime,
      createAction,
      category?.id,
      isStarred,
      selectedProject?.id,
      tabIndex,
      weeklyPlanId,
      relation,
      selectedPeople,
      toast,
      handleResetForm,
      createEvent,
      createPromises,
      createLeverageRequestWatcher,
      onCreateLeverageModalOpen,
      timeZone,
      t,
    ],
  );

  const openEmailModalOrSubmit = useCallback(
    (form: FormData) => {
      if (createLeverageRequestWatcher && !selectedPerson?.email) {
        onAddEmailModalOpen();
      } else {
        onSubmit(form);
      }
    },
    [onAddEmailModalOpen, onSubmit, createLeverageRequestWatcher, selectedPerson?.email],
  );

  const handleUserEnterFormSubmit = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        handleSubmit(openEmailModalOrSubmit)();
      }
    },
    [handleSubmit, openEmailModalOrSubmit],
  );

  useEffect(() => {
    let animationState: 'visible' | 'exit';

    if (!fancyCapturePage || isValid || isDirty) {
      // If from CategorySpotlight and user hasn't interacted, hide options
      if (isFromCategorySpotlight && !isValid && !isDirty) {
        animationState = 'exit';
        actionCreatorOptionsAnimation?.start('exit');
      } else {
        animationState = 'visible';
        actionCreatorOptionsAnimation?.start('visible');
      }
    } else {
      animationState = 'exit';
      actionCreatorOptionsAnimation?.start('exit');
    }

    onAnimationStateChange?.(animationState);
  }, [isValid, isDirty, actionCreatorOptionsAnimation, isFromCategorySpotlight, onAnimationStateChange]);

  useEffect(() => {
    return () => {
      handleResetForm(shouldFocus);
    };
    //!TODO: remove fix effect so we don't need to rely on eslint line below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category?.id, shouldFocus]);

  useEffect(() => {
    if (relation !== ActionRelation.Leverage) {
      setValue('createLeverageRequest', false);
    }
  }, [relation, setValue]);

  return (
    <VStack alignItems="start" gap={rem(24)} width="full" minWidth={0} maxWidth="100%">
      {fancyCapturePage && (
        <HStack textStyle="h4SatBlack" display="inline-block" width="full" color="text-primary">
          <Text as="span" width="fit-content" marginRight={rem(8)} wordBreak="break-word">
            {category?.role ? t('messages.readyToBe') : t('messages.readyTo')}
          </Text>

          <Text
            as="span"
            width="fit-content"
            color={isDarkMode ? category?.color : getColorFromToken(category?.color, undefined, '100')}
            wordBreak="break-word"
          >
            {category?.role ?? t('messages.achieveGoals')}
          </Text>

          <Text as="span" width="fit-content" marginRight={rem(8)} wordBreak="break-word">
            ?
          </Text>
        </HStack>
      )}
      <form
        onSubmit={handleSubmit(openEmailModalOrSubmit)}
        style={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <Box position="relative" zIndex={1}>
          <Controller
            name="name"
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <Textarea
                ref={mergeRefs(ref, textAreaValueRef)}
                autoComplete="off"
                placeholder={t('placeholders.captureAction')}
                value={value}
                borderRadius={rem(5)}
                isDisabled={isSubmitting}
                onChange={onChange}
                error={errors?.name?.message}
                errorMessageProps={{ marginTop: rem(-6), marginBottom: rem(6) }}
                _placeholder={{ color: 'text-primary', opacity: 0.5, textStyle: 'small' }}
                fontSize={rem(12)}
                fontWeight={500}
                lineHeight="130%"
                letterSpacing="0"
                marginBottom={rem(12)}
                backgroundColor={`${fancyCapturePage ? 'background-primary' : 'background-tertiary'}`}
                padding={`${rem(fancyCapturePage ? 16 : 7)} ${rem(10)}`}
                width="full"
                resize="none"
                border="none !important"
                onKeyDownCapture={handleUserEnterFormSubmit}
                rows={2}
                sx={{
                  caretColor: color,
                }}
              />
            )}
          />
        </Box>

        <motion.div
          initial="hidden"
          animate={actionCreatorOptionsAnimation}
          variants={ActionControleSlideYInOutVariants}
          transition={{ duration: 0.2 }}
          style={(() => {
            const shouldShowOptions = !fancyCapturePage || isValid || isDirty;
            const shouldHideInCategorySpotlight = isFromCategorySpotlight && !isValid && !isDirty;

            const isVisible = shouldShowOptions && !shouldHideInCategorySpotlight;

            return {
              position: 'relative',
              height: isVisible ? 'auto' : 0,
              zIndex: isVisible ? 1 : -1,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
            };
          })()}
        >
          <VStack
            align="start"
            gap={rem(8)}
            display={!fancyCapturePage || isDirty ? 'flex' : 'none'}
            width="full"
            minWidth={0}
            maxWidth="100%"
          >
            <VStack align="start" width="full" minWidth={0} maxWidth="100%">
              <HStack
                alignItems="center"
                flexWrap="wrap"
                rowGap={rem(8)}
                width="full"
                minWidth={0}
                maxWidth="100%"
                spacing={rem(8)}
              >
                {/* Input Duration */}
                <VStack align="start" flexShrink={0}>
                  <Controller
                    name="time"
                    control={control}
                    render={({ field: { value, onChange } }) => <InputDuration value={value} onChange={onChange} />}
                  />
                </VStack>

                {/* Date and Time */}
                  <RPMDateTimePickerWithPlaceholder
                    isDatePickerOpen={isDatePickerOpen}
                    scheduledTime={scheduledTime}
                    tabIndex={tabIndex}
                    onDatePickerOpen={onDatePickerOpen}
                    onClickUnplan={onClickUnplan}
                    dateRange={dateRange}
                    onChangeTabs={onChangeTabs}
                    onSave={handleDatePickerClose}
                    onCancel={onDatePickerClose}
                    scheduledDate={scheduledDate}
                    updateScheduledTime={handleScheduledTime}
                    updateSelectedDate={setScheduledDate}
                    withTimePicker={tabIndex === DAY}
                    selectedTimezone={selectedTimezone}
                    setSelectedTimezone={setSelectedTimezone}
                  />

                {/* Star button */}
                <IconStarred
                  starred={Boolean(isStarred)}
                  onClick={onStarringAction}
                  _hover={{ backgroundColor: 'background-quaternary' }}
                  minWidth={rem(28)}
                  minHeight={rem(28)}
                  height={rem(28)}
                  width={rem(28)}
                />

                {/* Leverage/commit button */}
                <ManageActionRelations
                  initialRelation={ActionRelation.None}
                  onRelationChange={setRelation}
                  onSelectPeople={setSelectedPeople}
                  shouldResetState={selectedPeople.length < 1}
                  selectedPeople={selectedPeople}
                />
              </HStack>

              {errors.time && (
                <Box as="span" marginTop={rem(-3)} color="red.500" fontSize="sm">
                  {errors.time.message}
                </Box>
              )}
              {/* TODO: remove this condition when SHOW_RECURRENT_EVENTS_FOR_ALL_USERS; SHOW_RECURRENT_EVENTS_FOR_BETA_USERS flags are removed */}
              {shouldShowRecurrenceFeature ? (
                <VStack alignItems="start" gap={rem(8)} width="full">
                  <Box width="50%" minWidth={0} maxWidth="50%">
                    <RecurrenceMenu
                      scheduleDate={scheduledDate ? scheduledDate : undefined}
                      variant="bordered"
                      recurrence={selectedRecurrence}
                      setSelectedRecurrence={setSelectedRecurrence}
                      readonly={!scheduledTime}
                      setCustomRecurrence={setCustomRecurrence}
                      width="full"
                      interval={customRecurrence?.interval ?? 1}
                      byDays={customRecurrence?.selectedWeekDays}
                      occurrences={
                        customRecurrence != null ? (customRecurrence.occurrences ?? undefined) : undefined
                      }
                      endDate={customRecurrence?.scheduledDate ?? null}
                      recurrenceFrequency={recurrenceFrequencyForMenu}
                    />
                  </Box>
                  <Box width="50%" minWidth={0} maxWidth="50%">
                    <ProjectsMenu
                      categoryId={selectedCategoryId}
                      project={selectedProject}
                      setSelectedProject={setSelectedProject}
                      showCategoryIcon={false}
                      width="full"
                    />
                  </Box>
                </VStack>
              ) : (
                <ProjectsMenu
                  categoryId={selectedCategoryId}
                  project={selectedProject}
                  setSelectedProject={setSelectedProject}
                  showCategoryIcon={false}
                  width="full"
                />
              )}

              <Controller
                name="createLeverageRequest"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    sx={{
                      '.chakra-checkbox__control[data-disabled] ': {
                        background: isDarkMode ? 'whiteAlpha.100' : 'gray.200 !important',
                      },
                    }}
                    marginTop={rem(4)}
                    isChecked={value}
                    isDisabled={!(relation === ActionRelation.Leverage && selectedPeople.length)}
                    onChange={onChange}
                    size="sm"
                  >
                    <Text as="span" textStyle="small">
                      {t('labels.createLeverageRequest')}
                    </Text>
                  </Checkbox>
                )}
              />
            </VStack>

            <HStack
              justifyContent="flex-end"
              flexWrap="wrap"
              rowGap={rem(8)}
              width="full"
              minWidth={0}
              maxWidth="100%"
              spacing={rem(8)}
            >
              <Button
                flexShrink={0}
                height={rem(28)}
                borderRadius={rem(8)}
                isDisabled={isDatePickerOpen}
                onClick={handleCancelClick}
                variant="tertiary"
              >
                <Text textStyle="smallBlack">{t('buttons.cancel')}</Text>
              </Button>
              <Button
                flexShrink={0}
                height={rem(28)}
                borderRadius={rem(4)}
                isDisabled={
                  !isValid || createAction.isLoading || createPromises.isLoading || !weeklyPlanId || isDatePickerOpen
                }
                isLoading={createAction.isLoading}
                leftIcon={<Icon as={IconPlus} boxSize={rem(14)} />}
                paddingX={rem(8)}
                type="submit"
                variant="primary"
              >
                <Text textStyle="smallBlack">{t('buttons.addAction')}</Text>
              </Button>
            </HStack>
          </VStack>
        </motion.div>
      </form>

      {selectedPerson && (
        <AddEmailToPersonModal
          isOpen={isAddEmailModalOpen}
          onClose={onAddEmailModalClose}
          person={selectedPerson}
          onSuccess={() => {
            handleSubmit(openEmailModalOrSubmit);
            onAddEmailModalClose();
          }}
        />
      )}
      {selectedPerson && leverageRequestPromise && (
        <CreateLeverageRequestModal
          isOpen={isCreateLeverageModalOpen}
          onClose={() => {
            onCreateLeverageModalClose();
            handleResetForm();
          }}
          title={getValues('name')}
          person={selectedPerson}
          dueDate={scheduledDate}
          tabIndex={tabIndex}
          leverageRequestPromise={leverageRequestPromise}
        />
      )}
    </VStack>
  );
}

export default QuickActionCreator;
